/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Spinner,
  ActivityTimeline,
  ActivityTimelineEvent,
  ActivityTimelineCasenote,
  ActivityTimelineMessage,
  ActivityTimelineEmail,
  ErrorBoundary,
  SvgIcon,
  Button,
  Modal,
  Warning
} from '../'

import {
  decodeBase64,
  encodeBase64,
  getNodeTypeFromBase64Id,
  isBase64,
  runQuery
} from '../../utils'

const initialState = {
  caseNotes: [],
  relatedMessages: [],
  relatedEmails: [],
  eventCreated: [],
  eventsChanged: []
}


class CaseActivity extends React.PureComponent {
  static propTypes = {
    caseId: PropTypes.string.isRequired,
    entryAdded: PropTypes.string,
    onAdd: PropTypes.func,
    assignNumberToUnknownSender: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    entryAdded: null,
    className: ''
  }

  state = initialState

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.entryAdded !== this.props.entryAdded) {
      // if an case activity item gets added, re-run only the necessary corresponding query
      this.refetchApprioriateActivityData(this.props.entryAdded)
    } else  if (prevProps.caseId !== this.props.caseId) {
      // if component has already been mounted and the case id prop changes, refetch all activity data and update state variables
      const caseId = decodeBase64(this.props.caseId)

      this.setState(
        initialState,
        async () => {
          const query = this.props.client.query

          const eventCreatedData = await query({
            query: getCaseActivityEventCreated,
            variables: { id: caseId }
          })

          const eventsChangedData = await query({
            query: getCaseActivityEventsChanged,
            variables: { id: caseId }
          })

          const caseNoteData = await query({
            query: getCaseActivityCaseNotes,
            variables: { id: caseId }
          })

          const messageData = await query({
            query: getCaseActivityMessages,
            variables: { id: caseId }
          })

          const emailData = await query({
            query: getCaseActivityEmails,
            variables: { id: caseId }
          })

          const eventCreated = this.getActivityItems(eventCreatedData.data.events.edges, 'eventCreated')
          const eventsChanged = this.getActivityItems(eventsChangedData.data.events.edges, 'eventsChanged')
          const caseNotes = this.getActivityItems(caseNoteData.data.casenotes.edges, 'caseNotes')
          const relatedMessages = this.getActivityItems(messageData.data.caserelations.edges, 'relatedMessages')
          const relatedEmails = this.getActivityItems(emailData.data.caserelations.edges, 'relatedEmails')

          this.setActivityItems(eventCreated, eventsChanged, caseNotes, relatedMessages, relatedEmails)
        }
      )
    } else if (this.props.eventCreated.events !== undefined &&
      this.props.eventsChanged.events !== undefined &&
      this.props.caseNotes.casenotes !== undefined &&
      this.props.relatedMessages !== undefined &&
      this.props.relatedEmails !== undefined) {

      this.initialiseFetchedActivitiyItems(prevProps, this.props)
    }
  }

  initialiseFetchedActivitiyItems = async (prevProps, props) => {
    // if activity data has been fetched initially, update state
    // also, if entries have been added and fetched, update state
    const eventCreated = await this.compileActivityItems(prevProps.eventCreated.events, props.eventCreated.events, 'eventCreated', null)
    const eventsChanged = await this.compileActivityItems(prevProps.eventsChanged.events, props.eventsChanged.events, 'eventsChanged', this.addEvent)
    const caseNotes = await this.compileActivityItems(prevProps.caseNotes.casenotes, props.caseNotes.casenotes, 'caseNotes', this.addCaseNote)
    const relatedMessages = await this.compileActivityItems(
      prevProps.relatedMessages.caserelations,
      props.relatedMessages.caserelations,
      'relatedMessages',
      props.relatedMessages.caserelations && props.relatedMessages.caserelations.edges.length > 0
        ? messages => this.addMessage(messages, props.relatedMessages.caserelations.edges[0].node.objectId)
        : null
    )
    const relatedEmails = await this.compileActivityItems(prevProps.relatedEmails.caserelations,
      props.relatedEmails.caserelations,
      'relatedEmails',
      props.relatedEmails.caserelations && props.relatedEmails.caserelations.edges.length > 0
        ? emails => this.addEmail(emails, props.relatedEmails.caserelations.edges[0].node.objectId)
        : null
    )

    this.setActivityItems(eventCreated, eventsChanged, caseNotes, relatedMessages, relatedEmails)
  }

  setActivityItems = (eventCreated, eventsChanged, caseNotes, relatedMessages, relatedEmails) => {
    let newState = Object.assign(
      {},
      eventCreated,
      eventsChanged,
      caseNotes,
      relatedMessages,
      relatedEmails
    )
    this.setState(newState)
  }

  refetchApprioriateActivityData = (entryAdded) => {
    if (isBase64(entryAdded)) {
      let entryNodeType = getNodeTypeFromBase64Id(entryAdded)
      switch (entryNodeType) {
        case 'CaseNoteNode':
          this.props.caseNotes.refetch()
          break

        case 'MessageNode':
          this.props.relatedMessages.refetch()
          break

        case 'EmailNode':
          this.props.relatedEmails.refetch()
          break

        default:
          break
      }
    } else {
      this.props.eventsChanged.refetch()
    }
  }

  compileActivityItems = async (prevData, data, activityType, addFunc) => {
    if (this.props[activityType].loading === false && this.props[activityType].networkStatus !== 4) {  // data has been fetched
      let noNewDataFetched = JSON.stringify(prevData) === JSON.stringify(data)
      if (
            !noNewDataFetched ||
            (
              noNewDataFetched &&
              this.state[activityType].length === 0  // data has not been set in state
            )
          ) {

        if (this.state[activityType].length === 0 && data.edges.length > 0) {
          let items = await this.getActivityItems(data.edges, activityType)
          return items
        } else if (addFunc) {
          let items = await addFunc(data.edges) // gets activity items after an item has been added
          return items
        }
      }
    }

    return {}
  }

  activityTypeRenderComponent = (type, item, key) => (
    {
      'eventCreated':
        <ActivityTimelineEvent
          event={item}
          key={key}
        />,
      'eventsChanged':
        <ActivityTimelineEvent
          event={item}
          key={key}
        />,
      'caseNotes':
        <ActivityTimelineCasenote
          casenote={item}
          key={key}
        />,
      'relatedMessages':
        <ErrorBoundary
          errorText='Failed to load timeline entry for inbound message.'
          fillOutErrorReport={true}
          className="slds-p-around_large"
          key={key}>
          <ActivityTimelineMessage
            message={item}
            case={this.props.caseId}
            onAdd={this.props.onAdd}
            assignNumberToUnknownSender={this.props.assignNumberToUnknownSender}
          />
        </ErrorBoundary>,
      'relatedEmails':
        <ActivityTimelineEmail
          email={item}
          case={this.props.caseId}
          onAdd={this.props.onAdd}
          key={key}
        />
    }[type]
  )

  getDate = (nodeType, id) => {
    if (nodeType === 'MessageNode') {
      return this.getMessageDate(id)
    } else {
      return this.getEmailDate(id)
    }
  }

  getMessageDate = (messageId) => (
    runQuery('message', [{ name: 'id', type: 'ID!', value: messageId }], ['id', 'createdAt']) // runQuery parameters: model to query, args, fields to return
  )

  getEmailDate = (emailId) => (
    runQuery('email', [{ name: 'id', type: 'ID!', value: emailId }], ['id', 'createdAt'])  // runQuery parameters: model to query, args, fields to return
  )

  addCaseNote = (caseNoteData) => {
    if (caseNoteData.length > this.state.caseNotes.length) {
      let newCaseNote = caseNoteData[0].node
      let caseNotes = Object.assign([], this.state.caseNotes)
      caseNotes.push({
        date: newCaseNote.createdAt,
        component: <ActivityTimelineCasenote
          casenote={newCaseNote}
          key={`caseActivity_caseNotes_${newCaseNote.id}`}
        />
      })

      return new Promise((resolve, reject) => resolve({ caseNotes }))
    }
  }

  addEvent = (events) => {
    if (events.length > this.state.eventsChanged.length) {
      let newEvent = events[0].node
      let eventsChanged = Object.assign([], this.state.eventsChanged)
      eventsChanged.push({
        date: newEvent.createdAt,
        component: <ActivityTimelineEvent
          event={newEvent}
          key={`caseActivity_eventsChanged_${newEvent.id}`}
        />
      })

      return new Promise((resolve, reject) => resolve({ eventsChanged }))
    }

  }

  addMessage = (messages, objectId) => {
    if (messages.length > this.state.relatedMessages.length) {
      let caseMessageId = encodeBase64(objectId, 'MessageNode')

      this.getMessageDate(caseMessageId).then(({ data }) => {
        let relatedMessages = Object.assign([], this.state.relatedMessages)
        relatedMessages.push({
          date: data.message.createdAt,
          component: <ErrorBoundary
            errorText='Failed to load timeline entry for inbound message.'
            fillOutErrorReport={true}
            className="slds-p-around_large">
            <ActivityTimelineMessage
              message={caseMessageId}
              case={this.props.caseId}
              onAdd={this.props.onAdd}
            />
          </ErrorBoundary>
        })

        return new Promise((resolve, reject) => resolve({ relatedMessages }))
      })
    }
  }

  addEmail = (emails, objectId) => {
    if (emails.length > this.state.relatedEmails.length) {
      let caseEmailId = encodeBase64(objectId, 'EmailNode')

      this.getEmailDate(caseEmailId).then(({ data }) => {
        let relatedEmails = Object.assign([], this.state.relatedEmails)
        relatedEmails.push({
          date: data.email.createdAt,
          component: <ActivityTimelineEmail
            email={caseEmailId}
            case={this.props.caseId}
            onAdd={this.props.onAdd}
          />
        })

        return new Promise((resolve, reject) => resolve({ relatedEmails }))
      })
    }
  }

  getActivityItems = (items, type) => {
    // handle the loading of event and case note activity items
    if (['eventCreated', 'eventsChanged', 'caseNotes'].indexOf(type) > -1) {
      let newState = {
        [type]: items.map(item => (
          {
            date: item.node.createdAt,
            component: this.activityTypeRenderComponent(type, item.node, `caseActivity_${type}_${item.node.id}`)
          }
        ))
      }

      return new Promise((resolve, reject) => resolve(newState))
    } else {  // handle the loading of message/email activity items
      let nodeType = type === 'relatedMessages'
        ? 'MessageNode'
        : 'EmailNode'
      // first get ids
      let caseMessageIds = items.map(caseRelation => encodeBase64(caseRelation.node.objectId, nodeType))
      // then fetch 'createdAt' date for each id
      let caseMessageDatePromises = caseMessageIds.map(messageId => (
        this.getDate(nodeType, messageId)
      ))

      let caseMessages = []

      return Promise.all(caseMessageDatePromises)
      .then(responses => {
        responses.forEach(response => {
          // now that we've got the date for each message, compile the necessary object item to pass to ActivityTimeline
          let message = nodeType === 'MessageNode'
            ? response.data.message
            : response.data.email

          caseMessages.push({
            date: message.createdAt,
            component: this.activityTypeRenderComponent(type, message.id, `caseActivity_${type}_${message.id}`)
          })
        })

        let newState = { [type]: caseMessages }
        return newState
      })
    }
  }

  refetchFailedDataLoads = (eventCreated, eventsChanged, caseNotes, relatedMessages, relatedEmails) => {
    if (eventCreated.error) {
      eventCreated.refetch()
    }
    if (eventsChanged.error) {
      eventsChanged.refetch()
    }
    if (caseNotes.error) {
      caseNotes.refetch()
    }
    if (relatedMessages.error) {
      relatedMessages.refetch()
    }
    if (relatedEmails.error) {
      relatedEmails.refetch()
    }
  }

  displayCaseActivityLoadingStates = () => (
    <Modal
      body={
        <div className='u-font-size-larger slds-m-left_small'>
          <div className='u-font-size-large u-font-weight-bold slds-m-bottom_medium'>
            Case information
            {this.caseActivityLoadingErrors() &&
              <Button
                type='reset'
                onClick={this.refetchFailedDataLoads(this.props.eventCreated, this.props.eventsChanged, this.props.caseNotes, this.props.relatedMessages, this.props.relatedEmails)}
                className='slds-m-left_small'>
                Retry loading
              </Button>
            }
          </div>
          <LoadingState
            data={this.props.eventCreated}
            loadingFeedback='Loading create event...'
            errorFeedback='Error loading create event.'
            successFeedback='Create event loaded.'
          />
          <LoadingState
            data={this.props.eventsChanged}
            loadingFeedback='Loading change events...'
            errorFeedback='Error loading change events.'
            successFeedback='Change events loaded.'
          />
          <LoadingState
            data={this.props.caseNotes}
            loadingFeedback='Loading case notes...'
            errorFeedback='Error loading case notes.'
            successFeedback='Case notes loaded.'
          />
          <LoadingState
            data={this.props.relatedMessages}
            loadingFeedback='Loading messages...'
            errorFeedback='Error loading messages.'
            successFeedback='Messages loaded.'
          />
          <LoadingState
            data={this.props.relatedEmails}
            loadingFeedback='Loading emails...'
            errorFeedback='Error loading emails.'
            successFeedback='Emails loaded.'
          />
        </div>
      }
    />
  )

  caseActivityContentLoading = () => (
    (this.props.caseNotes.loading || this.props.eventCreated.loading || this.props.eventsChanged.loading ||
    this.props.relatedMessages.loading || this.props.relatedEmails.loading) || (
      this.props.caseNotes.networkStatus === 4 || this.props.eventCreated.networkStatus === 4 ||
      this.props.eventsChanged.networkStatus === 4 || this.props.relatedMessages.networkStatus === 4 ||
      this.props.relatedEmails.networkStatus === 4
    )
  )

  caseActivityLoadingErrors = () => (
    this.props.caseNotes.error || this.props.eventCreated.error || this.props.eventsChanged.error ||
    this.props.relatedMessages.error || this.props.relatedEmails.error
  )

  render() {
    if (this.state.relatedMessages && this.state.relatedEmails) {
      return (
        <div className={`case-timeline-container stage-main slds-p-top_medium slds-p-right_xx-large slds-p-bottom_xx-large slds-p-left_xx-large ${this.props.className}`}>
          {this.caseActivityContentLoading() || this.caseActivityLoadingErrors()
            ? this.displayCaseActivityLoadingStates()
            : null
          }
          <ActivityTimeline
            items={[
              ...this.state.eventCreated,
              ...this.state.eventsChanged,
              ...this.state.caseNotes,
              ...this.state.relatedMessages,
              ...this.state.relatedEmails
            ]}
            groupByDates={true}
            key={Date.now()}
          />
        </div>
      )
    } else {
      return <Spinner className="slds-is-relative" />
    }
  }
}

const LoadingState = ({ data, loadingFeedback, successFeedback, errorFeedback }) => (
  data.loading || data.networkStatus === 4
    ? <LoadingInProgress feedback={loadingFeedback} />
    : data.error
      ? <LoadingError feedback={errorFeedback} onRetryClick={data.refetch} />
      : <LoadingSuccess feedback={successFeedback} />
)

const LoadingInProgress = ({ feedback }) => (
  <div className='slds-grid slds-m-bottom_x-small slds-m-left_x-small'>
    <Spinner type='brand' className='slds-is-relative' size='x-small' />
    <div className='slds-m-left_large'>{feedback}</div>
  </div>
)

const LoadingSuccess = ({ feedback }) => (
  <div className='slds-grid slds-m-bottom_x-small'>
    <SvgIcon
      variant='utility'
      symbol='check'
      size='x-small'
      colour='success'
    />
    <div className='slds-m-left_medium'>{feedback}</div>
  </div>
)

const LoadingError = ({ feedback, onRetryClick }) => (
  <Warning className='slds-grid slds-m-bottom_xx-small' iconSize='x-small'>
    <div className='slds-m-left_medium'>
      {feedback}
      {/* <Button
        type='reset'
        onClick={onRetryClick}
        className='slds-m-left_small'>
        Retry
      </Button> */}
    </div>
  </Warning>
)

const getCaseActivityCaseNotes = gql`
  query($id: UUID) {
    casenotes(case_Id: $id, orderBy: "-created_at") {
      edges {
        node {
          ...CaseNote
        }
      }
    }
  }
  ${fragments.casenote}
`
const getCaseActivityEventCreated = gql`
  query($id: UUID) {
    events(sourceId: $id, eventType: "model.created") {
      edges {
        node {
          id
          eventType
          eventData
          createdAt
        }
      }
    }
  }
`
const getCaseActivityEventsChanged = gql`
  query($id: UUID) {
    events(sourceId: $id, eventType: "model.updated", orderBy: "-created_at") {
      edges {
        node {
          id
          eventType
          eventData
          createdAt
        }
      }
    }
  }
`
const getCaseActivityMessages = gql`
  query($id: UUID) {
    caserelations(case_Id: $id, contentType_Model: "message", orderBy: "-created_at") {
      edges {
        node {
          id
          objectId
        }
      }
    }
  }
`
const getCaseActivityEmails = gql`
  query($id: UUID) {
    caserelations(case_Id: $id, contentType_Model: "email", orderBy: "-created_at") {
      edges {
        node {
          id
          objectId
        }
      }
    }
  }
`

const withCaseNotes = graphql(getCaseActivityCaseNotes, {
  name: 'caseNotes',
  options: ({ caseId }) => ({
    variables: {
      id: decodeBase64(caseId)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
})

const withEventCreated = graphql(getCaseActivityEventCreated, {
  name: 'eventCreated',
  options: ({ caseId }) => ({
    variables: {
      id: decodeBase64(caseId)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
})

const withEventsChanged = graphql(getCaseActivityEventsChanged, {
  name: 'eventsChanged',
  options: ({ caseId }) => ({
    variables: {
      id: decodeBase64(caseId)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
})

const withMessages = graphql(getCaseActivityMessages, {
  name: 'relatedMessages',
  options: ({ caseId }) => ({
    variables: {
      id: decodeBase64(caseId)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
})

const withEmails = graphql(getCaseActivityEmails, {
  name: 'relatedEmails',
  options: ({ caseId }) => ({
    variables: {
      id: decodeBase64(caseId)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
})

export default compose(
  withCaseNotes,
  withEventCreated,
  withEventsChanged,
  withMessages,
  withEmails,
  withApollo
)(CaseActivity)



// WEBPACK FOOTER //
// ./src/components/case-activity/index.js